import React, { useState, useEffect, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { toast, ToastContainer } from "react-toastify";
import api from "../../api";
import Footer from "../../Footer";
import useDynamicPageSize from "../../useDynamicPageSize";
import nodeApi from "../../nodeApi";
import { Spin } from "antd";

const AppStoreSettings: React.FC = () => {
    const routes = all_routes;
    const pageSize = useDynamicPageSize();

    const [playStoreUrl, setPlayStoreUrl] = useState("");
    const [appStoreUrl, setAppStoreUrl] = useState("");
    const [isFetching, setIsFetching] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [tableHeight, setTableHeight] = useState<number>(0);

    useLayoutEffect(() => {
        const calculateHeight = () => {
            const baseOffset = 194;
            const extraOffset = window.innerWidth <= 991 ? -43 : -81;
            const fixedOffset = baseOffset + extraOffset;
            setTableHeight(window.innerHeight - fixedOffset);
        };

        calculateHeight();
        window.addEventListener("resize", calculateHeight);
        return () => window.removeEventListener("resize", calculateHeight);
    }, []);

    useEffect(() => {
        fetchUrls();
    }, []);

    const fetchUrls = async () => {
        try {
            const response = await nodeApi<any>("/settings/app-urls", { method: "GET" });

            if (response?.data) {
                setPlayStoreUrl(response.data.playStoreUrl ?? "");
                setAppStoreUrl(response.data.appStoreUrl ?? "");
            }
        } catch (error) {
            console.error("Failed to fetch app URLs", error);
            toast.error("Failed to load current store links.");
        } finally {
            setIsFetching(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            // Final fallback trim verification
            const cleanPlayUrl = playStoreUrl.trim();
            const cleanAppUrl = appStoreUrl.trim();

            const payload = {
                playStoreUrl: cleanPlayUrl === "" ? null : cleanPlayUrl,
                appStoreUrl: cleanAppUrl === "" ? null : cleanAppUrl
            };

            await nodeApi("/settings/app-urls", {
                method: "POST",
                body: payload,
            });

            toast.success("App store URLs updated successfully!");
            fetchUrls();
        } catch (error) {
            toast.error("Failed to update URLs.");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card shadow-sm border-0">

                            <div className="card-header border-bottom bg-transparent py-3">
                                <h4 className="card-title mb-0 fw-bold text-slate-800">Mobile App Links</h4>
                                <p className="text-muted mb-0 mt-1">Configure user routing directions for Google Play Store and Apple App Store download listings</p>
                            </div>

                            <div className="card-body mt-2 position-relative" style={{ minHeight: "220px" }}>

                                {/* Unified Standalone Absolute Table Spinner Overlay Layout */}
                                {isFetching && (
                                    <div
                                        className="position-absolute d-flex align-items-center justify-content-center rounded-bottom-2xl"
                                        style={{
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: "rgba(255, 255, 255, 0.75)",
                                            zIndex: 5,
                                        }}
                                    >
                                        <Spin />
                                    </div>
                                )}

                                <form onSubmit={handleSave}>
                                    <div className="row">

                                        {/* Play Store */}
                                        <div className="col-md-6 form-group mb-3">
                                            <label className="fw-semibold text-secondary text-sm mb-1.5">Google Play Store URL</label>
                                            <input
                                                type="text" // Swapped to 'text' temporarily so malformed space formats don't fail native browser URL checks while typing
                                                className="form-control"
                                                value={playStoreUrl}
                                                onChange={(e) => {
                                                    // THE FIX: Replace all ordinary spaces (\s) AND non-breaking spaces (\u00A0) globally
                                                    const sanitizedValue = e.target.value.replace(/[\s\u00A0]+/g, "");
                                                    setPlayStoreUrl(sanitizedValue);
                                                }}
                                                placeholder="e.g. https://play.google.com/store/apps/details?id=com.company"
                                            />
                                        </div>

                                        {/* App Store */}
                                        <div className="col-md-6 form-group mb-3">
                                            <label className="fw-semibold text-secondary text-sm mb-1.5">Apple App Store URL</label>
                                            <input
                                                type="text" // Swapped to 'text' temporarily so malformed space formats don't fail native browser URL checks while typing
                                                className="form-control"
                                                value={appStoreUrl}
                                                onChange={(e) => {
                                                    // THE FIX: Replace all ordinary spaces (\s) AND non-breaking spaces (\u00A0) globally
                                                    const sanitizedValue = e.target.value.replace(/[\s\u00A0]+/g, "");
                                                    setAppStoreUrl(sanitizedValue);
                                                }}
                                                placeholder="e.g. https://apps.apple.com/us/app/your-app/id123456"
                                            />
                                        </div>

                                    </div>

                                    <div className="text-end mt-4 pt-3 border-top">
                                        <button
                                            type="submit"
                                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                                            disabled={isLoading}
                                        >
                                            {isLoading && (
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                            )}
                                            {isLoading ? "Saving..." : "Save Changes"}
                                        </button>
                                    </div>
                                </form>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </div>
    );
};

export default AppStoreSettings;